import { call, put, all, takeEvery, takeLatest, select } from "redux-saga/effects";
import { authActions, generalActions } from "../actions";
import api from "../services/auth.services";
import ACTIONS from "../constants";
import { TOAST_TYPE } from "Constants/constant";
import { groupByPermissionsByName } from "Utils/utils";
import GetItemFromLocalstorage from "Utils/GetItemFromLocalstorage";
import {
  applyRolePermissionIdsFromBroadcast,
  notifyPermissionsRefreshed,
  hasSpAdminLevelAccessFromUser,
} from "helpers/RolesPermission";
import { patchPlanFeaturesRoleCounts } from "helpers/planFeatures";
import { getRoleAndPermissionsList } from "store/selector/auth.selector";
import { stopPendingLeaveBadgeLifecycle } from "helpers/pendingLeaveBadge";
import { resolveOtpNavigation } from "./authOtpNavigation.utils";

function* login(state) {
  // try {
  //   yield put(authActions.loginAttempt.pending);
  //   const response = yield call(api.loginRequest, state.payload);
  //   if (response.access) {
  //     yield localStorage.setItem("accessToken", response.access);
  //     const userData = yield call(api.getProfile);
  //     userData.access = response.access;
  //     userData.refresh = response.refresh;
  //     yield localStorage.setItem("user", JSON.stringify(userData));
  //     yield put(authActions.loginAttempt.success(response));
  //   }
  // } catch (error) {
  //   yield put(authActions.loginAttempt.error(error));
  // }
}

function* logout() {
  try {
    yield put(authActions.logoutAttempt.pending);
    // Flush journey events and end session before clearing storage
    try {
      const { default: JourneyEventBuffer } = yield import("Utils/JourneyEventBuffer");
      const { default: SessionManager } = yield import("Utils/SessionManager");
      if (SessionManager.isActive()) {
        const startTime = sessionStorage.getItem("journey_session_start");
        let durationSeconds = null;
        if (startTime) {
          durationSeconds = Math.round(
            (Date.now() - new Date(startTime).getTime()) / 1000
          );
        }
        JourneyEventBuffer.track("session_end", {
          event_data: { duration_seconds: durationSeconds },
        });
        yield JourneyEventBuffer.flush();
      }
      JourneyEventBuffer.stop();
      SessionManager.endSession();
    } catch (e) {
      // Journey tracking errors should never block logout
    }
    yield call(api.logoutRequest);
    stopPendingLeaveBadgeLifecycle();
    yield localStorage.clear();
    yield sessionStorage.clear();
    yield put(authActions.logoutAttempt.success());
  } catch (error) {
    stopPendingLeaveBadgeLifecycle();
    yield localStorage.clear();
    yield sessionStorage.clear();
    yield put(authActions.logoutAttempt.error());
  }
}

function* reSendOTPSaga(state) {
  try {
    yield put(authActions.reSendOTP.pending);
    const response = yield call(api.resendOTPApi, state.payload);
    if (response) {
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.SUCCESS,
          title: "OTP Sent Successfully!",
        })
      );
      yield put(authActions.reSendOTP.success(response));
    }
  } catch (error) {
    yield put(
      generalActions.showToast({
        type: TOAST_TYPE.ERROR,
        title: error.message,
      })
    );
    yield put(authActions.reSendOTP.error(error));
  }
}

function* sendOTPForConfirmSaga(state) {
  try {
    yield put(authActions.sendOTPForConfirmation.pending);

    const response = yield call(api.sendOTPForConfirmationApi, state.payload);
    if (response) {
      yield localStorage.setItem("2FARequired", true);
      yield localStorage.setItem("step", "3");
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.SUCCESS,
          title: "OTP Verified Successfully!",
        })
      );
      yield put(authActions.sendOTPForConfirmation.success(response));
    }
  } catch (error) {
    yield put(
      generalActions.showToast({
        type: TOAST_TYPE.ERROR,
        title: error.message,
      })
    );
    yield put(authActions.sendOTPForConfirmation.error(error));
  }
}

function* sendOTPSaga(state) {
  try {
    yield put(authActions.sendOTP.pending);
    const response = yield call(api.sendOTPApi, state.payload);
    if (response) {
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.SUCCESS,
          title: "OTP Sent Successfully!",
        })
      );
      localStorage.setItem("attempt", "0");

      const navigation = resolveOtpNavigation({
        via: state.payload.via,
        phone: state.payload.phone,
        twoFaSetup: localStorage.getItem("2FASetup"),
        twoFaRequired: localStorage.getItem("2FARequired"),
      });

      if (navigation) {
        localStorage.setItem("step", navigation.step);
        if (navigation.twoFaSetup) {
          localStorage.setItem("2FASetup", navigation.twoFaSetup);
        }
        state.payload.navigate(navigation.path);
      }

      yield put(authActions.sendOTP.success(response));
    }
  } catch (error) {
    const message = String(error?.message || "");
    if (message.toLowerCase().includes("code already send")) {
      localStorage.setItem("step", "2.2");
      state.payload.navigate("/verify-otp");
      yield put(authActions.sendOTP.success(null));
      return;
    }

    yield put(
      generalActions.showToast({
        type: TOAST_TYPE.ERROR,
        title: error.message,
      })
    );
    yield put(authActions.sendOTP.error(error));
  }
}

function* resendEmailVerificatioCode(state) {
  try {
    yield put(authActions.resendEmailVerificatioCode.pending);
    const response = yield call(api.resendEmailVerificatioCode, state.payload);

    if (response) {
      yield put(
        generalActions.showToast({
          type: TOAST_TYPE.SUCCESS,
          title:
            response?.message ??
            "Email verification code successfully send, Check your email",
        })
      );
      yield put(authActions.resendEmailVerificatioCode.success(response));
      if (state?.callback) {
        state.callback();
      }
    }
  } catch (error) {
    yield put(
      generalActions.showToast({
        type: TOAST_TYPE.ERROR,
        title: error?.message ?? "Something went wrong!",
      })
    );
    yield put(authActions.resendEmailVerificatioCode.error(error));
  }
}

function* roleAndPermissionCreateOrUpdate(state) {
  const isUpdateCall = Object.prototype.hasOwnProperty.call(
    state.payload,
    "id"
  );

  try {
    yield put(authActions.roleAndPermisisonCreatedOrUpdated.pending);
    const response = yield call(
      api.roleAndPermissionCreateOrUpdateApi,
      state.payload
    );

    if (!response) {
      throw new Error("No response from server");
    }

    const roleData =
      response.data ?? (isUpdateCall ? { id: state.payload.id } : null);

    if (!roleData) {
      throw new Error("Invalid response from server");
    }

    yield put(
      generalActions.showToast({
        type: TOAST_TYPE.SUCCESS,
        title:
          response?.message ??
          `Role and their permissions ${
            isUpdateCall ? "updated" : "created"
          } successfully!`,
      })
    );
    yield put(
      authActions.roleAndPermisisonCreatedOrUpdated.success(
        roleData,
        isUpdateCall
      )
    );

    if (!isUpdateCall && roleData?.type) {
      const rolesList = yield select(getRoleAndPermissionsList);
      const user = yield GetItemFromLocalstorage("user");
      if (user && Array.isArray(rolesList)) {
        const updatedUser = patchPlanFeaturesRoleCounts(
          user,
          rolesList,
          roleData.type
        );
        yield localStorage.setItem("user", JSON.stringify(updatedUser));
        yield put(authActions.authenticatedUser(updatedUser));
      }
    }

    if (isUpdateCall) {
      const user = yield GetItemFromLocalstorage("user");
      const updatedRoleId = state.payload.id;
      const permissionIds = state.payload.permissions;
      const isSameRole =
        user?.role_id &&
        String(user.role_id) === String(updatedRoleId);
      if (
        isSameRole &&
        Array.isArray(permissionIds)
      ) {
        const patched = applyRolePermissionIdsFromBroadcast(
          updatedRoleId,
          permissionIds
        );
        if (patched) {
          yield put(authActions.authenticatedUser(patched));
        }
      }
      if (isSameRole) {
        yield put(authActions.roleAuthenticateUserRequest());
      }
    }
  } catch (error) {
    yield put(
      generalActions.showToast({
        type: TOAST_TYPE.ERROR,
        title: error?.message ?? "Something went wrong!",
      })
    );
    yield put(authActions.roleAndPermisisonCreatedOrUpdated.error(error));
  }
}

function* roleAndPermissionList(state) {
  try {
    yield put(authActions.rolesAndPermissionslist.pending);
    const response = yield call(api.roleAndPermissionList, state.payload);
    if (response && response.data) {
      yield put(authActions.rolesAndPermissionslist.success(response.data));

      const user = yield GetItemFromLocalstorage("user");
      const portalType = state.payload?.type;
      if (user && portalType && Array.isArray(response.data)) {
        const updatedUser = patchPlanFeaturesRoleCounts(
          user,
          response.data,
          portalType
        );
        const resolved =
          updatedUser?.payment_setting?.plan_features?.roles?.[portalType];
        const previous =
          user?.payment_setting?.plan_features?.roles?.[portalType]?.current;
        if (resolved && resolved.current !== previous) {
          yield localStorage.setItem("user", JSON.stringify(updatedUser));
          yield put(authActions.authenticatedUser(updatedUser));
        }
      }
    }
  } catch (error) {
    yield put(
      generalActions.showToast({
        type: TOAST_TYPE.ERROR,
        title: error?.message ?? "Something went wrong!",
      })
    );
    yield put(authActions.rolesAndPermissionslist.error(error));
  }
}

function* defaultRoleAndPermission(state) {
  try {
    yield put(authActions.defaultRolesAndPermissions.pending);
    const response = yield call(api.defaultRoleAndPermission, state.payload);
    if (response) {
      const isEdit = Object.prototype.hasOwnProperty.call(state.payload, "id");
      const data = [
        ...response?.catagorizePermission,
        ...response.generalPermission,
      ].map((item) => ({
        ...item,
        isChecked: isEdit ? Boolean(item.status) : true,
        children:
          item?.children?.map((y) => ({
            ...y,
            isChecked: isEdit ? Boolean(y.status) : true,
          })) || [],
      }));

      yield put(
        authActions.defaultRolesAndPermissions.success({
          permissions: data,
          role: response?.role ?? null,
        })
      );
    }
  } catch (error) {
    yield put(
      generalActions.showToast({
        type: TOAST_TYPE.ERROR,
        title: error?.message ?? "Something went wrong!",
      })
    );
    yield put(authActions.defaultRolesAndPermissions.error(error));
  }
}

function* roleAuthenticateUser() {
  try {
    const response = yield call(api.roleAuthenticateUser);
    if (response && response.data) {
      let user = yield GetItemFromLocalstorage("user");

      // Only the true SP owner skips granular permissions.
      // Tenant Super Admin managers (is_manager_admin) must keep role toggles.
      const isTrueSpOwner =
        user &&
        !user.is_manager_admin &&
        (user.userType === "sp_admin" ||
          localStorage.getItem("userType") === "sp_admin");

      if (isTrueSpOwner) {
        const hadPermissions = Boolean(user?.permissions);
        if (hadPermissions) {
          delete user.permissions;
          yield localStorage.setItem("user", JSON.stringify(user));
          yield put(authActions.authenticatedUser(user));
          notifyPermissionsRefreshed();
        }
        return;
      }

      user.permissions = groupByPermissionsByName(
        response?.data?.permissions || []
      );
      if (response.data.role_id != null) {
        user.role_id = response.data.role_id;
      }
      yield localStorage.setItem("user", JSON.stringify(user));
      yield put(authActions.authenticatedUser(user));
      notifyPermissionsRefreshed();
    }
  } catch (error) {
    console.log("error", error?.message || error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(ACTIONS.RESEND_OTP_CALL, reSendOTPSaga),
    takeEvery(ACTIONS.SEND_OTP_FOR_CONFIRMATION_CALL, sendOTPForConfirmSaga),
    takeEvery(ACTIONS.SEND_OTP_CALL, sendOTPSaga),
    takeEvery(ACTIONS.LOGIN_CALL, login),
    // takeEvery(ACTIONS.LOGOUT_BEGIN, logout)
    takeEvery(ACTIONS.LOGOUT_BEGIN, logout),
    takeEvery(
      ACTIONS.RESEND_VERIFICATION_CODE_CALL,
      resendEmailVerificatioCode
    ),
    takeLatest(
      ACTIONS.ROLE_AND_PERMISSION_CREATE_OR_UPDATE_CALL,
      roleAndPermissionCreateOrUpdate
    ),
    takeEvery(ACTIONS.ROLE_AND_PERMISSION_LIST_CALL, roleAndPermissionList),
    takeEvery(
      ACTIONS.DEFAULT_ROLE_AND_PERMISSION_CALL,
      defaultRoleAndPermission
    ),
    takeLatest(ACTIONS.ROLE_AUTHENTICATE_USER_CALL, roleAuthenticateUser),
  ]);
}
